import { type ClassValue, clsx } from "clsx";
import { formatDate as formatDateFns } from "date-fns";
import { id } from "date-fns/locale";
import { twMerge } from "tailwind-merge";
import * as XLSX from "xlsx";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function populateMenu(user: any) {
  const features = user.features;

  if (!features) return [];
  const menu = features
    .filter((item: any) => !item.parent_feature)
    .map((item: any) => {
      item.children = features.filter(
        (child: any) => child.parent_feature === item.feature
      );
      return item;
    });

  return menu;
}

// getAccessPage has been removed — permissions are now read from usePermissionStore

type GetFormatDate = {
  date: Date | string;
  locale?: Intl.LocalesArgument;
  formatOptions?: Intl.DateTimeFormatOptions;
};

export function getFormatedDate(
  date: Date | string,
  locale: Intl.LocalesArgument = "id-ID",
  formatOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  }
) {
  if (typeof date === "string") date = new Date(date);
  return new Intl.DateTimeFormat(locale, formatOptions)
    .format(date)
    .replace(/(\d{2})\.(\d{2})/, "$1:$2");
}

export function formatDate(date: string | Date, format?: string, opts?: {}) {
  format = format ?? "dd MMM yyyy HH:mm bb";
  return formatDateFns(date, format, { locale: id, ...opts });
}

type FormatCurrency = {
  amount: number;
  locale?: string;
  currencySymbol?: string;
};

export function formatCurrency(params: FormatCurrency): string {
  let amount = params.amount;
  let locale = params.locale ?? "id-ID";
  let currencySymbol = params.currencySymbol ?? "IDR";

  if (typeof amount !== "number" || isNaN(amount)) {
    console.error(`Invalid amount: ${amount}`);
    return amount.toString(); // Fallback to plain number string
  }
  if (typeof locale !== "string" || typeof currencySymbol !== "string") {
    console.error(
      `Invalid locale or currencySymbol: ${locale}, ${currencySymbol}`
    );
    return amount.toString(); // Fallback to plain number string
  }
  if (amount === 0) return "0";
  if (amount < 0) {
    console.error(`Negative amount: ${amount}`);
    return amount.toString(); // Fallback to plain number string
  }
  if (amount > 1000000000) {
    console.error(`Amount too large: ${amount}`);
    return amount.toString(); // Fallback to plain number string
  }
  if (amount < 0.01) {
    console.error(`Amount too small: ${amount}`);
    return amount.toString(); // Fallback to plain number string
  }

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencySymbol,
      currencyDisplay: "symbol", // or 'code' or 'name'
      minimumFractionDigits: 0,
    });
    return formatter.format(amount);
  } catch (error) {
    console.error(
      `Error formatting currency for locale "${locale}" and symbol "${currencySymbol}":`,
      error
    );
    return amount.toString(); // Fallback to plain number string
  }
}

export function formatTextWithLineBreaks(text: string): string {
  const words = text.split(" ");
  let formattedText = "";
  for (let i = 0; i < words.length; i++) {
    formattedText += `${words[i]} `;
    if ((i + 1) % 3 === 0) {
      formattedText += "\n";
    }
  }
  return formattedText.trimEnd();
}

/**
 * Converts seconds to hours (as a floating point number).
 * @param seconds Number of seconds
 * @returns Number of hours
 */
export function secondsToHours(seconds: number): number {
  if (typeof seconds !== "number" || Number.isNaN(seconds) || seconds < 0)
    return 0;
  return seconds / 3600;
}

/**
 * Create delay to simulate loading state.
 * @param delay number of miliseconds
 * @returns Promise void
 */
export async function createSimulateDelay(delay?: number) {
  if (!delay) delay = 1000;
  await new Promise((resolve) => setTimeout(resolve, delay));
}

export function formatNumber(value: number | string): string {
  const number = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(number)) return "0";

  return number.toLocaleString("id-ID", {
    maximumFractionDigits: 0,
    useGrouping: true,
  });
}

export const downloadFile = (
  blobData: BlobPart,
  fileName: string,
  mimeType = "application/octet-stream"
) => {
  const blob = new Blob([blobData], { type: mimeType });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;

  // link tidak terlihat dan tidak mengganggu layout
  link.style.display = "none";
  document.body.appendChild(link);

  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

interface NormalizedError {
  statusCode: number | null;
  message: string;
}

export function normalizeError(err: unknown): NormalizedError {
  let statusCode: number | null = null;
  let message = "Unknown error";

  if (typeof err === "object" && err !== null) {
    const anyErr = err as any;

    // Axios response error
    if ("response" in anyErr && anyErr.response) {
      statusCode = anyErr.response.status ?? null;

      // 1. validation error: response.data.errors[0].message
      if (
        Array.isArray(anyErr.response.data?.errors) &&
        anyErr.response.data.errors.length > 0
      ) {
        message = anyErr.response.data.errors[0].message;
      }
      // 2. general API error: response.data.message
      else if (anyErr.response.data?.message) {
        message = anyErr.response.data.message;
      }
      // fallback to err.message
      else if (anyErr.message) {
        message = anyErr.message;
      } else {
        message = "Server error";
      }

      return { statusCode, message };
    }

    // Axios request made but no response
    if ("request" in anyErr) {
      message = "No response from server";
      return { statusCode: null, message };
    }
  }

  // native JS Error
  if (err instanceof Error) {
    message = err.message;
    return { statusCode: null, message };
  }

  // fallback
  return { statusCode: null, message: String(err) };
}

export const extractFilename = (url: string) => {
  if (!url) return "downloaded-file";

  // 1. Hapus query string
  const cleanUrl = url.split("?")[0];

  // 2. Ambil segment terakhir
  const parts = cleanUrl.split("/");
  let filename = parts[parts.length - 1];

  if (!filename) return "downloaded-file";

  // 3. Trim whitespace
  filename = filename.trim();

  // 4. Hapus karakter ilegal
  // Windows tidak mengizinkan: < > : " / \ | ? *
  filename = filename.replace(/[<>:"/\\|?*]/g, "");

  // 5. Hapus trailing dot atau underscore
  filename = filename.replace(/[\._]+$/, "");

  // 6. Tambahkan extension default jika tidak ada
  if (!filename.includes(".")) filename += ".file";

  // 7. fallback
  if (!filename) filename = "downloaded-file.file";

  return filename;
};

export const formatWIB = (date: Date, isEnd = false) => {
  const d = new Date(date);

  if (!isEnd) {
    d.setHours(0, 0, 0, 0); // 00:00:00
  } else {
    d.setHours(23, 59, 59, 999); // 23:59:59
  }

  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    `${d.getFullYear()}-` +
    `${pad(d.getMonth() + 1)}-` +
    `${pad(d.getDate())}T` +
    `${pad(d.getHours())}:` +
    `${pad(d.getMinutes())}:` +
    `${pad(d.getSeconds())}+07:00`
  );
};

export const formatWIBOffset = (date: Date, isEnd = false): string => {
  const d = new Date(date); // clone, aman

  if (isEnd) {
    d.setHours(23, 59, 59, 0);
  } else {
    d.setHours(0, 0, 0, 0);
  }

  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    `${d.getFullYear()}-` +
    `${pad(d.getMonth() + 1)}-` +
    `${pad(d.getDate())}T` +
    `${pad(d.getHours())}:` +
    `${pad(d.getMinutes())}:` +
    `${pad(d.getSeconds())}+07:00`
  );
};

export function extractFilenameFromPath(url: any) {
  if (typeof url !== "string") return "";

  const match = url.match(/[^/]+$/);
  return match ? match[0] : "";
}

export const openGoogleMaps = (
  latitude: number,
  longitude: number,
  label: string
) => {
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}&query_place_id=${label}`;
  window.open(mapsUrl, "_blank");
};

type Params = { [key: string]: any };

export function cleanUpParams(
  params: Params = {},
  excludeEmptyValue: boolean = true
): Params {
  const result: Params = {};

  Object.entries(params).forEach(([key, value]) => {
    if (excludeEmptyValue) {
      if (value) result[key] = value;
    } else {
      result[key] = value;
    }
  });

  return result;
}

export function paramsToString(
  params = {},
  excludeEmptyValue = true,
  sort = true
) {
  const cleanParams = cleanUpParams(params, excludeEmptyValue);
  const paramsArray: any[] = [];

  Object.entries(cleanParams).forEach((param) => {
    paramsArray.push(`${param[0]}=${param[1]}`);
  });

  if (sort) paramsArray.sort();

  return paramsArray.length ? `?${paramsArray.join("&")}` : "";
}

export const isExpired = (value?: string | Date | null) => {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.getTime() < Date.now();
};

export function getPaginationRange(
  current: number,
  total: number
): (number | string)[] {
  const delta = 1;
  const range: (number | string)[] = [];

  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  range.push(1);

  if (left > 2) range.push("...");

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < total - 1) range.push("...");

  if (total > 1) range.push(total);

  return range;
}

export function normalizeExcelDate(value: any): string | undefined {
  if (value == null || value === "") return undefined;

  if (typeof value === "string") return value;

  if (typeof value === "number") {
    const d = XLSX.SSF.parse_date_code(value);
    if (!d) return undefined;
    return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(
      2,
      "0"
    )}`;
  }

  if (value instanceof Date) {
    return value.toISOString().split("T")[0];
  }

  return undefined;
}
